/**
 * =============================================================
 * AADHAV – STUDENT PERFORMANCE SELECTORS (PRODUCTION READY)
 * =============================================================
 */

/**
 * 🔥 BASE STATE (SAFE ACCESS)
 * Adjusted to match Redux store registration (e.g., state.admin.studentPerformance or state.studentPerformance)
 */
const selectPerformanceState = (state) =>
  state?.admin?.studentPerformance ?? state?.studentPerformance ?? {};

/**
 * 🔥 PERFORMANCE DATA (ALWAYS ARRAY)
 * Returns all tournaments linked to the student with their nested games
 */
export const selectPerformanceData = (state) => {
  const data = selectPerformanceState(state).performanceData;
  return Array.isArray(data) ? data : [];
};

/**
 * 🔥 SINGLE TOURNAMENT STATE (OBJECT OR NULL)
 * Returns the currently loaded single tournament detail record
 */
export const selectSingleTournament = (state) => {
  return selectPerformanceState(state).singleTournament ?? null;
};

/**
 * 🔥 OPENINGS LIST (ALWAYS ARRAY)
 * Returns master list of chess openings fetched from database
 */
export const selectOpeningsList = (state) => {
  const list = selectPerformanceState(state).openingsList;
  return Array.isArray(list) ? list : [];
};

/**
 * 🔥 SERVER-SIDE SUMMARY (FROM API)
 * Mirrors tournament participants style summary handling
 */
export const selectPerformanceSummary = (state) => {
  const summary = selectPerformanceState(state).summary;
  return {
    totalTournaments: Number(summary?.total_tournaments ?? 0),
    totalWins: Number(summary?.total_wins ?? 0),
    totalPoints: Number(summary?.total_points ?? 0),
  };
};

/**
 * 🔥 LOADING STATE
 */
export const selectPerformanceLoading = (state) =>
  Boolean(selectPerformanceState(state).loading);

/**
 * 🔥 ERROR STATE
 */
export const selectPerformanceError = (state) =>
  selectPerformanceState(state).error ?? null;

/**
 * 🔥 META (SAFE DEFAULT)
 */
export const selectPerformanceMeta = (state) => {
  const meta = selectPerformanceState(state).meta;
  return {
    total: meta?.total ?? 0,
    total_pages: meta?.total_pages ?? 1,
    page: meta?.page ?? 1,
    limit: meta?.limit ?? 10,
  };
};

/**
 * 🔥 DERIVED: GET TOURNAMENT BY ID
 * Useful for editing a specific tournament performance record (checks loaded list or single view)
 */
export const selectTournamentById = (state, tournamentId) => {
  const targetId = Number(tournamentId);
  const single = selectSingleTournament(state);

  if (single && Number(single.id) === targetId) {
    return single;
  }

  return selectPerformanceData(state).find((t) => Number(t.id) === targetId);
};

/**
 * 🔥 DERIVED: GET ALL GAMES FOR A SPECIFIC TOURNAMENT
 * Useful for the "Game Log" table in your UI
 */
export const selectGamesByTournament = (state, tournamentId) => {
  const tournament = selectTournamentById(state, tournamentId);
  return Array.isArray(tournament?.games) ? tournament.games : [];
};

/**
 * 🔥 DERIVED: AGGREGATE SUMMARY METRICS
 * Calculates lifetime stats (Total wins, Total games) from all records
 */
export const selectStudentAggregateStats = (state) => {
  const tournaments = selectPerformanceData(state);

  return tournaments.reduce(
    (acc, t) => {
      acc.totalScore += Number(t.final_score || 0);
      acc.totalRounds += Number(t.total_rounds || 0);
      acc.totalWins += Number(t.white_wins || 0) + Number(t.black_wins || 0);
      acc.tournamentCount += 1;
      return acc;
    },
    { totalScore: 0, totalRounds: 0, totalWins: 0, tournamentCount: 0 }
  );
};

/**
 * 🔥 DERIVED: FAILURE TAG ANALYSIS
 * Returns a count of mistakes strictly from tournament-level primary failure tags across all tournaments.
 * Handles comma-separated multi-select tags (e.g. "TACTICAL_BLUNDER,TIME_TROUBLE") as well as array or string formats.
 */
export const selectFailureTagStats = (state) => {
  const tournaments = selectPerformanceData(state);
  const stats = {};

  tournaments.forEach((t) => {
    // Extract primary failure tags from tournament master record
    let primaryTags = [];
    if (Array.isArray(t.primary_failure_tag)) {
      primaryTags = t.primary_failure_tag;
    } else if (typeof t.primary_failure_tag === "string") {
      primaryTags = t.primary_failure_tag.split(",").map((s) => s.trim());
    }

    primaryTags.forEach((tag) => {
      const cleanTag = tag || "NONE";
      if (cleanTag !== "NONE" && cleanTag !== "") {
        stats[cleanTag] = (stats[cleanTag] || 0) + 1;
      }
    });
  });

  return stats;
};